import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createArticle,
  deleteArticle,
  getArticle,
  getArticles,
  publishArticle,
  unpublishArticle,
  updateArticle,
} from '../lib/knowledge-api'

export function articlesQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: ['articles', workspaceId] as const,
    queryFn: async () => {
      const result = await getArticles(workspaceId)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: Boolean(workspaceId),
  })
}

export function articleQueryOptions(
  workspaceId: string,
  articleId: string | undefined,
) {
  return queryOptions({
    queryKey: ['articles', workspaceId, articleId] as const,
    queryFn: async () => {
      const result = await getArticle(workspaceId, articleId!)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: Boolean(workspaceId && articleId),
  })
}

export function useArticles(workspaceId: string) {
  return useQuery(articlesQueryOptions(workspaceId))
}

export function useArticle(workspaceId: string, articleId: string | undefined) {
  return useQuery(articleQueryOptions(workspaceId, articleId))
}

export function useCreateArticle(workspaceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { collectionId: string; title?: string }) => {
      const result = await createArticle(workspaceId, payload)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles', workspaceId] })
    },
  })
}

export function useUpdateArticle(workspaceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: string
      title?: string
      content?: Record<string, unknown>
      collectionId?: string
    }) => {
      const result = await updateArticle(workspaceId, id, payload)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['articles', workspaceId, data.id], data)
      queryClient.invalidateQueries({ queryKey: ['articles', workspaceId] })
    },
  })
}

export function useDeleteArticle(workspaceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteArticle(workspaceId, id)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles', workspaceId] })
    },
  })
}

export function usePublishArticle(workspaceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await publishArticle(workspaceId, id)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['articles', workspaceId, data.id], data)
      queryClient.invalidateQueries({ queryKey: ['articles', workspaceId] })
    },
  })
}

export function useUnpublishArticle(workspaceId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await unpublishArticle(workspaceId, id)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['articles', workspaceId, data.id], data)
      queryClient.invalidateQueries({ queryKey: ['articles', workspaceId] })
    },
  })
}

export function getArticlesByCollection(
  articles: ReturnType<typeof useArticles>['data'],
  collectionId: string,
) {
  return articles?.filter((a) => a.collectionId === collectionId) ?? []
}
